/**
 * Global runtime guards for Helencia.
 *
 * React ErrorBoundary catches *render* errors. It does NOT catch:
 *   - unhandled Promise rejections (e.g. fetch().then() without .catch())
 *   - errors thrown inside event handlers / setTimeout
 *   - errors from third-party scripts loaded async
 *
 * In development CRA bundles `react-error-overlay` which shows the giant
 * "Uncaught runtime errors:" red overlay for any of those. The Emergent preview
 * runs the dev build, so end users see it too. We register our handlers in the
 * CAPTURE phase and call stopImmediatePropagation so the dev-overlay listener
 * never fires — instead the user sees a small toast.
 *
 * Behaviour in production builds is identical: errors are logged + a toast
 * appears, but the page never goes blank.
 *
 * We ALSO patch Node.prototype.removeChild / insertBefore to be defensive.
 * This is the canonical fix for facebook/react#11538 — when Google Translate
 * (or any extension) mutates the DOM, React's commit phase fails with
 * "NotFoundError: The object can not be found here." on iOS Safari/Chrome.
 */
import { toast } from "sonner";
import { reportClientError } from "@/lib/clientErrorLogger";

let installed = false;

/* ============================================================
 * 1) Defensive Node.prototype patches — FIX for Google Translate
 *    breaking React on iOS Safari (facebook/react#11538).
 *    Must run BEFORE React mounts.
 * ============================================================ */
function patchNodePrototypes() {
  if (typeof Node === "undefined" || !Node.prototype) return;
  if (Node.prototype.__helenciaPatched) return;
  Node.prototype.__helenciaPatched = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function patchedRemoveChild(child) {
    if (child && child.parentNode !== this) {
      // Google Translate (or another extension) re-parented this node.
      // Returning the child without throwing keeps React's commit phase
      // alive. React will reconcile on the next render.
      // eslint-disable-next-line no-console
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[Helencia] removeChild: child not in this parent — ignoring (likely auto-translate).");
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchedInsertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // eslint-disable-next-line no-console
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[Helencia] insertBefore: reference not in this parent — appending instead.");
      }
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

function isBenign(err) {
  if (!err) return true;
  const message = String(err.message || err.reason?.message || err);
  if (!message) return true;
  return (
    message.includes("ResizeObserver loop") ||
    message.includes("Script error") ||
    message.includes("Non-Error promise rejection captured") ||
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError")
  );
}

function pickUserMessage(reason) {
  if (!reason) return "Une erreur est survenue.";
  if (reason.userMessage) return reason.userMessage;
  if (reason.response?.data?.detail && typeof reason.response.data.detail === "string") {
    return reason.response.data.detail;
  }
  // Don't expose raw stack traces; show a friendly default.
  return "Une action a échoué. Réessayez ou rafraîchissez la page.";
}

export function installRuntimeGuards() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Patch Node.prototype FIRST so React commits are resilient to extensions
  // and auto-translate (Google Translate on iOS Safari).
  patchNodePrototypes();

  const handleRejection = (event) => {
    const reason = event.reason;
    // eslint-disable-next-line no-console
    console.warn("[Helencia] unhandled rejection:", reason);
    if (isBenign(reason)) {
      event.preventDefault();
      event.stopImmediatePropagation?.();
      return;
    }
    reportClientError({
      type: "unhandledrejection",
      message: String(reason?.message || reason || ""),
      stack: reason?.stack || "",
      url: typeof window !== "undefined" ? window.location.href : "",
    });
    try {
      toast.error(pickUserMessage(reason));
    } catch (_) {
      /* toast may not be ready before React mounts; ignore */
    }
    event.preventDefault();
    event.stopImmediatePropagation?.();
  };

  const handleError = (event) => {
    // eslint-disable-next-line no-console
    console.warn("[Helencia] window error:", event.error || event.message);
    if (isBenign(event.error || event)) {
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      return;
    }
    reportClientError({
      type: "window-error",
      message: String(event.error?.message || event.message || ""),
      stack: event.error?.stack || "",
      url: typeof window !== "undefined" ? window.location.href : "",
      extra: {
        filename: event.filename || "",
        lineno: event.lineno || 0,
        colno: event.colno || 0,
      },
    });
    // Don't preventDefault on real errors — let ErrorBoundary handle them when
    // they happen during render. But we DO stop the dev overlay from firing.
    event.stopImmediatePropagation?.();
  };

  // capture:true so we run BEFORE react-error-overlay's listeners.
  window.addEventListener("unhandledrejection", handleRejection, { capture: true });
  window.addEventListener("error", handleError, { capture: true });

  // ----- Hide the webpack-dev-server / CRA error overlay aggressively -----
  // CRA injects an iframe `webpack-dev-server-client-overlay` (or a `<div>` in
  // older builds) every time a runtime error fires. Even after we silence the
  // event, the overlay still reads the error from the dev server SSE channel
  // and shows the giant red panel. We watch for it and hide it on sight.
  const hideOverlay = (node) => {
    try {
      const id = (node?.id || "") + " " + (node?.getAttribute?.("name") || "");
      const cls = node?.className || "";
      if (
        id.includes("webpack-dev-server-client-overlay") ||
        id.includes("react-refresh") ||
        (typeof cls === "string" && cls.includes("react-error-overlay"))
      ) {
        node.remove();
        return true;
      }
      // Some builds inject a plain <div> with `Uncaught runtime errors:` text
      const text = node?.innerText || node?.textContent || "";
      if (typeof text === "string" && text.startsWith("Uncaught runtime errors")) {
        node.remove();
        return true;
      }
    } catch (_) {
      /* ignore */
    }
    return false;
  };

  // Sweep what is already there.
  document.querySelectorAll("iframe, body > div").forEach(hideOverlay);

  // Watch for new injections.
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes?.forEach((n) => {
        if (n.nodeType === 1) hideOverlay(n);
      });
    }
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });
}
